// Overgezet uit naslag/server/server/player.ts (de methodes uit server/main.ts).
import { SpelFout } from '../contract.js';
import { minBy } from '../hulp.js';
import { pasUpdateToe } from '../mongo.js';
import { skipPlayerTask } from '../data/taken.js';
/**
 * Niet in het origineel: een naam zonder spaties eromheen en in kleine letters, om namen te
 * vergelijken. "anna" en "Anna " zijn dezelfde speler.
 */
export const vergelijkbareNaam = (naam) => naam.trim().toLowerCase();
/** Niet in het origineel: de naam zonder spaties eromheen. Een lege naam kan niet. */
export const geldigeNaam = (username) => {
    const naam = typeof username === 'string' ? username.trim() : '';
    if (!naam)
        throw new SpelFout('Vul een naam in.');
    return naam;
};
/**
 * Geeft een speler een naam. Niet in het origineel: de naam komt er zonder spaties eromheen
 * in. Een naam die alleen in hoofdletters of spaties verschilt van een bestaande naam, bestaat
 * al.
 */
export const setPlayerName = (staat, _ctx, playerId, username) => {
    const naam = geldigeNaam(username);
    const nameTaken = staat.players.players.some((speler) => speler.name !== undefined && vergelijkbareNaam(speler.name) === vergelijkbareNaam(naam));
    if (nameTaken)
        throw new SpelFout('Deze naam bestaat al!');
    pasUpdateToe(staat.players, {
        $set: {
            'players.$.name': naam,
        },
    }, { selector: { players: { $elemMatch: { _id: playerId } } } });
};
export const setTasksForPlayerDB = (staat, _ctx, player) => {
    // Niet in het origineel: de server rekent de nieuwe hand uit zijn eigen staat uit, in plaats
    // van de lijsten van de browser over te nemen. Een tweede klik (een tweede apparaat, een late
    // herhaling) of een klik die kruist met het weghalen van een speler kan zo geen kaart
    // dupliceren of kwijtmaken.
    const huidig = staat.players.players.find((speler) => speler._id === player?._id);
    if (!huidig)
        throw new SpelFout('Deze speler hoort niet bij dit spel.');
    if (huidig.sharedTasks.length > 0)
        throw new SpelFout('Je hebt je taken al ingebracht.');
    if (staat.game.tasksOnTable.length > 0 || staat.game.effectQueue.length > 0) {
        throw new SpelFout('Je kunt nu geen taken inbrengen.');
    }
    const gevraagd = Array.isArray(player.sharedTasks) ? player.sharedTasks : [];
    const ingebracht = [...new Set(gevraagd)].filter((id) => huidig.tasks.includes(id));
    pasUpdateToe(staat.players, {
        $set: {
            'players.$[element].tasks': huidig.tasks.filter((id) => !ingebracht.includes(id)),
            'players.$[element].sharedTasks': ingebracht.length > 0 ? ingebracht : [skipPlayerTask._id],
        },
    }, {
        selector: { players: { $elemMatch: { _id: huidig._id } } },
        arrayFilters: [{ 'element._id': huidig._id }],
    });
};
/**
 * Zet de gedeelde taken van een speler terug in zijn eigen stapel en maakt de gekozen
 * machines leeg. De client-wrapper in het origineel haalde `_skipPlayer` eruit voordat hij
 * de methode aanriep; omdat elke aanroep via die wrapper liep, doet de actie dat nu zelf.
 */
export const moveTasksBackWithinPlayer = (staat, _ctx, playerId, tasksToMoveBack) => {
    pasUpdateToe(staat.players, {
        $push: {
            'players.$[element].tasks': {
                $each: tasksToMoveBack.filter((t) => t !== skipPlayerTask._id),
            },
        },
        $set: {
            'players.$[element].sharedTasks': [],
            sharedSelectedTasksAssets: [],
        },
    }, {
        selector: { players: { $elemMatch: { _id: playerId } } },
        arrayFilters: [{ 'element._id': playerId }],
    });
};
export const selectSharedTasks = (staat, _ctx, tasksSelected) => {
    pasUpdateToe(staat.players, {
        $set: {
            sharedSelectedTasks: tasksSelected,
        },
    });
};
export const setSpendBudget = (staat, _ctx, spendBudget) => {
    pasUpdateToe(staat.players, {
        $set: {
            spendBudget,
        },
    });
};
function subdivide(items, n) {
    const result = Array.from({ length: n }, () => []);
    items.forEach((item, index) => {
        result[index % n]?.push(item);
    });
    return result;
}
/**
 * Haalt een speler uit het spel. Zijn taken (eigen en gedeelde) en de rollen die verder
 * niemand heeft, gaan naar de andere spelers.
 *
 * Niet in het origineel: heeft de speler al taken ingelegd en een ander ook, dan gaan zijn
 * ingelegde taken naar de gedeelde taken van die ander (die met de minste taken). Zo blijven
 * de gezamenlijke keuze en het uitgegeven budget kloppen. Het origineel stopte ze in een hand,
 * ook als ze gekozen waren: bij "Speel taken" lag zo'n kaart op tafel en in een hand. Het
 * plaatsje `_skipPlayer` gaat nergens meer heen.
 */
export const removePlayer = (staat, _ctx, playerId) => {
    // get all tasks of current player
    const players = staat.players.players;
    if (players.length === 0)
        throw new SpelFout('No players');
    const player = players.find((p) => p._id === playerId);
    if (!player)
        throw new SpelFout('Player not found');
    // Het origineel liep hier vast met een TypeError (delen door nul spelers). Een spel
    // zonder spelers kan niet verder, dus nu een melding.
    if (players.length === 1)
        throw new SpelFout('De laatste speler kan niet worden verwijderd');
    const ingelegd = player.sharedTasks.filter((taskId) => taskId !== skipPlayerTask._id);
    const ontvanger = minBy(players.filter((p) => p._id !== playerId && p.sharedTasks.length > 0), (p) => p.tasks.length);
    const tasks = ontvanger ? [...player.tasks] : [...player.tasks, ...ingelegd];
    const roles = [...player.role];
    // Niet in het origineel: rollen en kaarten gaan naar plekken met een speler. Een lege plek
    // (niemand ingelogd) kan er niets mee; het spel zou dan wachten op een rol die niemand heeft.
    const overig = players.filter((p) => p._id !== playerId);
    const metNaam = overig.filter((p) => p.name);
    const ontvangers = new Set((metNaam.length > 0 ? metNaam : overig).map((p) => p._id));
    // divide tasks over other players
    const remainingPlayers = ontvangers.size;
    const dividedTasks = subdivide(tasks, remainingPlayers);
    const $push = [];
    players.forEach((p, index) => {
        if (!ontvangers.has(p._id))
            return;
        const newTasks = dividedTasks.pop();
        if (!newTasks || newTasks.length === 0)
            return;
        $push.push([`players.${index}.tasks`, { $each: newTasks }]);
    });
    if (ontvanger && ingelegd.length > 0) {
        const index = players.indexOf(ontvanger);
        $push.push([`players.${index}.sharedTasks`, { $each: ingelegd }]);
    }
    // check if existing role(s) need distributing
    const existingRoles = players.filter((p) => p._id !== playerId).flatMap((p) => p.role);
    const rolesToDistribute = roles.filter((r) => !existingRoles.includes(r));
    // divide roles over other players
    const dividedRoles = subdivide(rolesToDistribute, remainingPlayers);
    players.forEach((p, index) => {
        if (!ontvangers.has(p._id))
            return;
        const newRoles = dividedRoles.pop();
        if (!newRoles || newRoles.length === 0)
            return;
        $push.push([`players.${index}.role`, { $each: newRoles }]);
    });
    // update game with new tasks and roles
    pasUpdateToe(staat.players, {
        $push: Object.fromEntries($push),
    });
    // remove player from game
    pasUpdateToe(staat.players, {
        $pull: { players: { _id: playerId } },
    });
};
